import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import "./ReportsSectionCompact.css";
import {
  postGiftCardsIssued,
  postGiftCardsBalancesAsOf,
  postGiftCardsClientsWithBalance,
  postGiftCardsSummary,
  downloadBlob,
} from "../api/reports";
import { getSalonTimezone } from "../utils/salonTimezone";
import InfoTooltip from "../components/common/InfoTooltip";
import SortableTh from "../components/common/SortableTh";
import ReportPageHeader from "../components/reports/ReportPageHeader";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import useTableSort from "../hooks/useTableSort";
import useHelpGuideNavigation from "../hooks/useHelpGuideNavigation";
import { GIFT_CARD_ANALYTICS_TOOLTIPS } from "../constants/reportTooltips";
import { REPORT_HELP } from "../constants/reportHelpContent";

const formatDateISO = (date, timeZone) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  if (timeZone) {
    try {
      const formatter = new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
      return formatter.format(date);
    } catch (err) {
      // fall back to local time
    }
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const addDays = (base, days) => {
  const next = new Date(base.getTime());
  next.setDate(next.getDate() + days);
  return next;
};

const formatMoney = (value) => {
  const amount = Number(value ?? 0);
  if (!Number.isFinite(amount)) return "$0.00";
  return `$${amount.toFixed(2)}`;
};

const formatNumber = (value, digits = 0) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "--";
  return num.toFixed(digits);
};

const ALERT_CLASS =
  "rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700";

const FIELD_CLASS =
  "h-10 px-3 text-sm md:text-base rounded-xl border border-slate-300 shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 disabled:bg-slate-50";

const LABEL_TEXT = "text-xs font-semibold uppercase tracking-wide text-slate-500";

const BTN_PRIMARY =
  "h-10 px-5 rounded-full bg-rose-500 text-white text-sm md:text-base font-semibold hover:bg-rose-600 disabled:opacity-60 disabled:cursor-not-allowed";

const BTN_OUTLINE =
  "h-10 px-5 rounded-full border border-slate-300 text-slate-700 text-sm md:text-base font-semibold hover:border-slate-400";

const BTN_EXPORT =
  "h-9 px-4 rounded-full border border-rose-200 text-rose-600 text-sm font-semibold hover:bg-rose-50";

const TAB_BASE =
  "h-9 md:h-10 px-4 rounded-full text-sm font-semibold transition";

const TAB_ACTIVE = "bg-rose-500 text-white";

const TAB_INACTIVE = "border border-slate-300 text-slate-700 hover:border-slate-400";

const TABS = [
  { key: "giftCardsSold", label: "Gift Cards Sold" },
  { key: "balancesAsOf", label: "Balances As Of" },
  { key: "clientsWithBalance", label: "Clients With Balance" },
  { key: "salesRedemptions", label: "Sales & Redemptions" },
  { key: "liabilitySummary", label: "Liability Summary" },
];

const normalizeNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const escapeCsv = (value) => {
  const str = String(value ?? "");
  if (str.includes('"') || str.includes(",") || str.includes("\n")) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

const exportCsv = (filename, columns, rows) => {
  if (!Array.isArray(columns) || columns.length === 0) return;
  const header = columns.map((col) => escapeCsv(col.label)).join(",");
  const data = (rows || []).map((row) =>
    columns.map((col) => escapeCsv(row?.[col.key] ?? "")).join(",")
  );
  const csv = [header, ...data].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  downloadBlob(blob, filename);
};

const buildIssuedTable = (rows) => {
  const columns = [
    { key: "issued_date", label: "Issued Date" },
    { key: "code", label: "Gift Card Code/ID" },
    { key: "client", label: "Client" },
    { key: "amount", label: "Issued Amount" },
    { key: "sold_by", label: "Sold By" },
  ];
  const dataRows = (rows || []).map((row) => ({
    issued_date: row.issued_at_local || "--",
    code: row.code || "--",
    client: row.client_name || "Anonymous",
    amount: formatMoney(row.issued_amount),
    sold_by: row.sold_by_employee || "--",
  }));
  return { columns, rows: dataRows };
};

const buildBalancesGiftCardTable = (rows) => {
  const columns = [
    { key: "code", label: "Gift Card Code/ID" },
    { key: "client", label: "Client" },
    { key: "balance", label: "Balance As Of" },
    { key: "last_activity", label: "Last Activity" },
  ];
  const dataRows = (rows || []).map((row) => ({
    code: row.code || "--",
    client: row.client_name || "Anonymous",
    balance: formatMoney(row.balance_as_of),
    last_activity: row.last_activity_local || "--",
  }));
  return { columns, rows: dataRows };
};

const buildBalancesClientTable = (rows) => {
  const columns = [
    { key: "client", label: "Client" },
    { key: "gift_cards_count", label: "Gift Cards Count" },
    { key: "balance", label: "Total Balance As Of" },
  ];
  const dataRows = (rows || []).map((row) => ({
    client: row.client_name || "Anonymous",
    gift_cards_count: row.gift_cards_count ?? 0,
    balance: formatMoney(row.total_balance_as_of),
  }));
  return { columns, rows: dataRows };
};

const buildClientsWithBalanceTable = (rows) => {
  const columns = [
    { key: "client", label: "Client" },
    { key: "balance", label: "Total Balance As Of End Date" },
    { key: "gift_cards_count", label: "Gift Cards Count" },
    { key: "last_activity", label: "Last Activity" },
  ];
  const dataRows = (rows || []).map((row) => ({
    client: row.client_name || "Anonymous",
    balance: formatMoney(row.total_balance_as_of_end_date),
    gift_cards_count: row.gift_cards_count ?? 0,
    last_activity: row.last_activity_local || "--",
  }));
  return { columns, rows: dataRows };
};

const buildTopClientsTable = (rows) => {
  const columns = [
    { key: "client", label: "Client" },
    { key: "balance", label: "Balance" },
  ];
  const dataRows = (rows || []).map((row) => ({
    client: row.client_name || "Client",
    balance: formatMoney(row.total_balance),
  }));
  return { columns, rows: dataRows };
};

const buildReconciliationTiles = (summary) => {
  const issuedTotal = Number(summary?.issued_total ?? 0);
  const redeemedTotal = Number(summary?.redeemed_total ?? 0);
  const outstandingRange = issuedTotal - redeemedTotal;
  return [
    { label: "Sold", value: formatMoney(issuedTotal) },
    { label: "Redeemed", value: formatMoney(redeemedTotal) },
    { label: "Outstanding (Range)", value: formatMoney(outstandingRange) },
    { label: "Liability (As Of Date)", value: formatMoney(summary?.liability_as_of) },
  ];
};

export default function GiftCardAnalyticsReport() {
  const { currentSalon } = useAuth();
  const { jumpToSectionWithHighlight, getSectionClassName } = useHelpGuideNavigation();
  const helpGuide = REPORT_HELP.GIFT_CARD_ANALYTICS;
  const salonTimezone = useMemo(
    () => getSalonTimezone(currentSalon),
    [currentSalon]
  );

  const today = formatDateISO(new Date(), salonTimezone);
  const defaultStart = formatDateISO(addDays(new Date(), -6), salonTimezone);

  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(today);
  const [asOfDate, setAsOfDate] = useState(today);
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");
  const [minBalance, setMinBalance] = useState("");
  const [maxBalance, setMaxBalance] = useState("");
  const [clientQuery, setClientQuery] = useState("");
  const [includeAnonymous, setIncludeAnonymous] = useState(true);
  const [groupBy, setGroupBy] = useState("gift_card");
  const [activeTab, setActiveTab] = useState("giftCardsSold");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [cache, setCache] = useState({});
  const [snapshotCache, setSnapshotCache] = useState({});
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [snapshotError, setSnapshotError] = useState("");
  const lastEndDateRef = useRef(endDate);

  useEffect(() => {
    if (!endDate) return;
    if (!asOfDate || asOfDate === lastEndDateRef.current) {
      setAsOfDate(endDate);
    }
    lastEndDateRef.current = endDate;
  }, [endDate, asOfDate]);

  const togglePillClass = (active) =>
    `${TAB_BASE} ${active ? TAB_ACTIVE : TAB_INACTIVE}`;

  const renderFieldLabel = (label, tooltipKey) => (
    <div className="flex items-center gap-1.5">
      <label className={LABEL_TEXT}>{label}</label>
      {tooltipKey ? (
        <InfoTooltip text={GIFT_CARD_ANALYTICS_TOOLTIPS[tooltipKey]} label={label} />
      ) : null}
    </div>
  );

  const renderInlineLabel = (label, tooltipKey) => (
    <div className="flex items-center gap-1.5">
      <span className={LABEL_TEXT}>{label}</span>
      {tooltipKey ? (
        <InfoTooltip text={GIFT_CARD_ANALYTICS_TOOLTIPS[tooltipKey]} label={label} />
      ) : null}
    </div>
  );

  const resetFilters = () => {
    setStartDate(defaultStart);
    setEndDate(today);
    setAsOfDate(today);
    setMinAmount("");
    setMaxAmount("");
    setMinBalance("");
    setMaxBalance("");
    setClientQuery("");
    setIncludeAnonymous(true);
    setGroupBy("gift_card");
  };

  const filters = useMemo(
    () => ({
      start_date: startDate,
      end_date: endDate,
      as_of_date: asOfDate,
      min_amount: normalizeNumber(minAmount),
      max_amount: normalizeNumber(maxAmount),
      min_balance: normalizeNumber(minBalance),
      max_balance: normalizeNumber(maxBalance),
      client_query: clientQuery.trim() ? clientQuery.trim() : null,
      include_anonymous: includeAnonymous,
      group_by: groupBy,
    }),
    [
      startDate,
      endDate,
      asOfDate,
      minAmount,
      maxAmount,
      minBalance,
      maxBalance,
      clientQuery,
      includeAnonymous,
      groupBy,
    ]
  );

  const cacheKey = useMemo(
    () => `${activeTab}:${JSON.stringify(filters)}`,
    [activeTab, filters]
  );

  const activeData = cache[cacheKey];
  const snapshotCacheKey = useMemo(
    () =>
      JSON.stringify({
        start_date: startDate,
        end_date: endDate,
        as_of_date: asOfDate,
      }),
    [startDate, endDate, asOfDate]
  );
  const snapshotData = snapshotCache[snapshotCacheKey] || null;

  useEffect(() => {
    if (!startDate || !endDate || !asOfDate) return;
    if (endDate < startDate) return;
    if (snapshotCache[snapshotCacheKey]) return;

    let ignore = false;
    setSnapshotLoading(true);
    setSnapshotError("");

    postGiftCardsSummary({
      start_date: startDate,
      end_date: endDate,
      as_of_date: asOfDate,
    })
      .then((data) => {
        if (ignore) return;
        setSnapshotCache((prev) => ({ ...prev, [snapshotCacheKey]: data }));
      })
      .catch((err) => {
        if (ignore) return;
        setSnapshotError(
          err?.response?.data?.detail || "Unable to load the gift card snapshot."
        );
      })
      .finally(() => {
        if (ignore) return;
        setSnapshotLoading(false);
      });

    return () => {
      ignore = true;
    };
  }, [startDate, endDate, asOfDate, snapshotCache, snapshotCacheKey]);

  const runReport = useCallback(async () => {
    if (!cacheKey || cache[cacheKey]) return;
    if (
      ["giftCardsSold", "clientsWithBalance", "salesRedemptions"].includes(
        activeTab
      )
    ) {
      if (!startDate || !endDate) {
        setError("Select a valid start and end date.");
        return;
      }
    }
    if (["balancesAsOf", "liabilitySummary"].includes(activeTab)) {
      if (!asOfDate) {
        setError("Select an as-of date.");
        return;
      }
    }
    setLoading(true);
    setError("");
    try {
      let data = null;
      if (activeTab === "giftCardsSold") {
        data = await postGiftCardsIssued({
          start_date: startDate,
          end_date: endDate,
          min_amount: normalizeNumber(minAmount),
          max_amount: normalizeNumber(maxAmount),
          client_query: clientQuery.trim() ? clientQuery.trim() : null,
          include_anonymous: includeAnonymous,
        });
      } else if (activeTab === "balancesAsOf") {
        data = await postGiftCardsBalancesAsOf({
          as_of_date: asOfDate,
          min_balance: normalizeNumber(minBalance),
          max_balance: normalizeNumber(maxBalance),
          group_by: groupBy,
          client_query: clientQuery.trim() ? clientQuery.trim() : null,
          include_zero: false,
          include_anonymous: includeAnonymous,
        });
      } else if (activeTab === "clientsWithBalance") {
        data = await postGiftCardsClientsWithBalance({
          start_date: startDate,
          end_date: endDate,
          min_balance: normalizeNumber(minBalance),
          client_query: clientQuery.trim() ? clientQuery.trim() : null,
        });
      } else if (activeTab === "salesRedemptions") {
        data = await postGiftCardsSummary({
          start_date: startDate,
          end_date: endDate,
          as_of_date: null,
        });
      } else if (activeTab === "liabilitySummary") {
        const fallbackStart = startDate || asOfDate;
        const fallbackEnd = endDate || asOfDate;
        data = await postGiftCardsSummary({
          start_date: fallbackStart,
          end_date: fallbackEnd,
          as_of_date: asOfDate,
        });
      }
      setCache((prev) => ({ ...prev, [cacheKey]: data }));
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to run report.");
    } finally {
      setLoading(false);
    }
  }, [
    cache,
    cacheKey,
    activeTab,
    startDate,
    endDate,
    asOfDate,
    minAmount,
    maxAmount,
    minBalance,
    maxBalance,
    clientQuery,
    includeAnonymous,
    groupBy,
  ]);

  const emptyState = (
    <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-6 text-sm text-slate-500">
      Run the report to load this tab.
    </div>
  );

  const issuedColumns = useMemo(
    () => ({
      issued_date: { type: "date", getValue: (row) => row?.issued_at_local },
      code: { type: "text", getValue: (row) => row?.code || "" },
      client: { type: "text", getValue: (row) => row?.client_name || "" },
      amount: { type: "number", getValue: (row) => row?.issued_amount },
      sold_by: { type: "text", getValue: (row) => row?.sold_by_employee || "" },
    }),
    []
  );

  const issuedSort = useTableSort({
    rows: activeData?.rows || [],
    columns: issuedColumns,
    fallbackKey: "issued_date",
  });

  const balancesGiftCardColumns = useMemo(
    () => ({
      code: { type: "text", getValue: (row) => row?.code || "" },
      client: { type: "text", getValue: (row) => row?.client_name || "" },
      balance: { type: "number", getValue: (row) => row?.balance_as_of },
      last_activity: {
        type: "date",
        getValue: (row) => row?.last_activity_local,
      },
    }),
    []
  );

  const balancesGiftCardSort = useTableSort({
    rows: activeData?.rows || [],
    columns: balancesGiftCardColumns,
    fallbackKey: "code",
  });

  const balancesClientColumns = useMemo(
    () => ({
      client: { type: "text", getValue: (row) => row?.client_name || "" },
      gift_cards_count: {
        type: "number",
        getValue: (row) => row?.gift_cards_count,
      },
      balance: { type: "number", getValue: (row) => row?.total_balance_as_of },
    }),
    []
  );

  const balancesClientSort = useTableSort({
    rows: activeData?.rows || [],
    columns: balancesClientColumns,
    fallbackKey: "client",
  });

  const clientsWithBalanceColumns = useMemo(
    () => ({
      client: { type: "text", getValue: (row) => row?.client_name || "" },
      balance: {
        type: "number",
        getValue: (row) => row?.total_balance_as_of_end_date,
      },
      gift_cards_count: {
        type: "number",
        getValue: (row) => row?.gift_cards_count,
      },
      last_activity: {
        type: "date",
        getValue: (row) => row?.last_activity_local,
      },
    }),
    []
  );

  const clientsWithBalanceSort = useTableSort({
    rows: activeData?.rows || [],
    columns: clientsWithBalanceColumns,
    fallbackKey: "client",
  });

  const topClientsColumns = useMemo(
    () => ({
      client: { type: "text", getValue: (row) => row?.client_name || "" },
      balance: { type: "number", getValue: (row) => row?.total_balance },
    }),
    []
  );

  const topClientsSort = useTableSort({
    rows: activeData?.top_clients_by_balance || [],
    columns: topClientsColumns,
    fallbackKey: "client",
  });

  const balancesRows =
    groupBy === "client" ? balancesClientSort.sortedRows : balancesGiftCardSort.sortedRows;

  return (
    <div className="reportSectionShell">
      <div className="reportSectionWide report-section-tight report-section-compact text-sm md:text-base">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <ReportPageHeader
            title="Gift Card Analytics"
            titleAddon={
              <InfoTooltip
                text={GIFT_CARD_ANALYTICS_TOOLTIPS.report_scope}
                label="Gift Card Analytics"
              />
            }
          />

          <div className="px-6 py-6 space-y-6">
            <div
              id="gift-card-analytics-overview"
              className={getSectionClassName("gift-card-analytics-overview")}
            >
              <ReportHelpCard
                title={helpGuide.title}
                subtitle={helpGuide.subtitle}
                sections={helpGuide.sections}
                troubleshooting={helpGuide.troubleshooting}
                storageKey="report_help_gift_card_analytics"
                onJumpToSection={jumpToSectionWithHighlight}
              />
            </div>
            {error && <div className={ALERT_CLASS}>{error}</div>}
            {loading && <div className={ALERT_CLASS}>Loading...</div>}

            <section className="space-y-4">
              <div>
                <div className="text-base md:text-lg font-semibold text-slate-900">
                  Gift Card Reconciliation Snapshot
                </div>
                <div className="mt-1 text-sm text-slate-600">
                  This loads automatically from the selected date range and as-of date.
                </div>
              </div>
              {snapshotError ? <div className={ALERT_CLASS}>{snapshotError}</div> : null}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                {buildReconciliationTiles(snapshotData).map((tile) => (
                  <div
                    key={tile.label}
                    className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
                  >
                    <div className="text-xs uppercase text-slate-500">{tile.label}</div>
                    <div className="text-2xl font-semibold text-slate-900">
                      {snapshotLoading && !snapshotData ? "Loading..." : tile.value}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <div
              id="gift-card-analytics-filters"
              className={`rounded-2xl border border-slate-200 bg-white shadow-sm p-5 space-y-4 ${getSectionClassName(
                "gift-card-analytics-filters"
              )}`}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                <div>
                  {renderFieldLabel("Start Date", "start_date")}
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                  />
                </div>
                <div>
                  {renderFieldLabel("End Date", "end_date")}
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                  />
                </div>
                <div>
                  {renderFieldLabel("As Of Date", "as_of_date")}
                  <input
                    type="date"
                    value={asOfDate}
                    onChange={(e) => setAsOfDate(e.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                  />
                </div>
                <div>
                  {renderFieldLabel("Client Search", "client_search")}
                  <input
                    type="text"
                    value={clientQuery}
                    onChange={(e) => setClientQuery(e.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                    placeholder="Name, phone, email"
                  />
                </div>
                <div>
                  {renderFieldLabel("Min Amount", "min_amount")}
                  <input
                    type="number"
                    value={minAmount}
                    onChange={(e) => setMinAmount(e.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                    placeholder="0"
                  />
                </div>
                <div>
                  {renderFieldLabel("Max Amount", "max_amount")}
                  <input
                    type="number"
                    value={maxAmount}
                    onChange={(e) => setMaxAmount(e.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                    placeholder="0"
                  />
                </div>
                <div>
                  {renderFieldLabel("Min Balance", "min_balance")}
                  <input
                    type="number"
                    value={minBalance}
                    onChange={(e) => setMinBalance(e.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                    placeholder="0"
                  />
                </div>
                <div>
                  {renderFieldLabel("Max Balance", "max_balance")}
                  <input
                    type="number"
                    value={maxBalance}
                    onChange={(e) => setMaxBalance(e.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={includeAnonymous}
                      onChange={(e) => setIncludeAnonymous(e.target.checked)}
                      className="h-4 w-4 rounded border-slate-300 text-rose-500 focus:ring-rose-500"
                    />
                    <span>Include anonymous</span>
                  </label>
                  <InfoTooltip
                    text={GIFT_CARD_ANALYTICS_TOOLTIPS.include_anonymous}
                    label="Include anonymous"
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={resetFilters}
                    className={BTN_OUTLINE}
                    disabled={loading}
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    onClick={runReport}
                    className={BTN_PRIMARY}
                    disabled={loading}
                  >
                    Run Report
                  </button>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {TABS.map((tab) => {
                const tooltip = GIFT_CARD_ANALYTICS_TOOLTIPS[`tab_${tab.key}`];
                return (
                  <div key={tab.key} className="flex items-center gap-1.5">
                    <button
                      type="button"
                      className={`${TAB_BASE} ${
                        activeTab === tab.key ? TAB_ACTIVE : TAB_INACTIVE
                      }`}
                      onClick={() => setActiveTab(tab.key)}
                    >
                      {tab.label}
                    </button>
                    {tooltip ? <InfoTooltip text={tooltip} label={tab.label} /> : null}
                  </div>
                );
              })}
            </div>

            <div
              id="gift-card-analytics-details"
              className={`space-y-6 ${getSectionClassName("gift-card-analytics-details")}`}
            >
              {activeTab === "giftCardsSold" && (
                <div className="space-y-6">
                  {!activeData ? (
                    emptyState
                  ) : (
                    <>
                      <div
                        id="gift-card-analytics-kpis"
                        className={`grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 ${getSectionClassName(
                          "gift-card-analytics-kpis"
                        )}`}
                      >
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                          <div className="text-xs uppercase text-slate-500">
                            Count Sold
                          </div>
                          <div className="text-2xl font-semibold text-slate-900">
                            {formatNumber(activeData?.totals?.count, 0)}
                          </div>
                        </div>
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                          <div className="text-xs uppercase text-slate-500">
                            Total Sold Amount
                          </div>
                          <div className="text-2xl font-semibold text-slate-900">
                            {formatMoney(activeData?.totals?.total_issued_amount)}
                          </div>
                        </div>
                      </div>

                      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                        <div
                          id="gift-card-analytics-export"
                          className={`flex items-center justify-between px-4 py-3 border-b border-slate-100 ${getSectionClassName(
                            "gift-card-analytics-export"
                          )}`}
                        >
                          <div className="text-sm font-semibold text-slate-700">
                            Issued Gift Cards
                          </div>
                          <button
                            type="button"
                            className={BTN_EXPORT}
                            onClick={() => {
                              const table = buildIssuedTable(issuedSort.sortedRows || []);
                              exportCsv(
                                `gift_cards_issued_${startDate}_to_${endDate}.csv`,
                                table.columns,
                                table.rows
                              );
                            }}
                          >
                            Export CSV
                          </button>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="min-w-full text-sm report-table">
                            <thead className="bg-slate-50 text-slate-600">
                              <tr>
                                <SortableTh
                                  label="Issued Date"
                                  sortKey="issued_date"
                                  sortState={issuedSort.sortState}
                                  onSort={issuedSort.handleSort}
                                  tooltip={GIFT_CARD_ANALYTICS_TOOLTIPS.column_issued_date}
                                  className="px-4 py-3 report-th-left"
                                  align="left"
                                />
                                <SortableTh
                                  label="Gift Card Code/ID"
                                  sortKey="code"
                                  sortState={issuedSort.sortState}
                                  onSort={issuedSort.handleSort}
                                  tooltip={GIFT_CARD_ANALYTICS_TOOLTIPS.column_code}
                                  className="px-4 py-3 report-th-left"
                                  align="left"
                                />
                                <SortableTh
                                  label="Client"
                                  sortKey="client"
                                  sortState={issuedSort.sortState}
                                  onSort={issuedSort.handleSort}
                                  tooltip={GIFT_CARD_ANALYTICS_TOOLTIPS.column_client}
                                  className="px-4 py-3 report-th-left"
                                  align="left"
                                />
                                <SortableTh
                                  label="Issued Amount"
                                  sortKey="amount"
                                  sortState={issuedSort.sortState}
                                  onSort={issuedSort.handleSort}
                                  tooltip={GIFT_CARD_ANALYTICS_TOOLTIPS.column_amount}
                                  className="px-4 py-3 report-th-center"
                                  align="center"
                                />
                                <SortableTh
                                  label="Sold By"
                                  sortKey="sold_by"
                                  sortState={issuedSort.sortState}
                                  onSort={issuedSort.handleSort}
                                  tooltip={GIFT_CARD_ANALYTICS_TOOLTIPS.column_sold_by}
                                  className="px-4 py-3 report-th-left"
                                  align="left"
                                />
                              </tr>
                            </thead>
                            <tbody>
                              {issuedSort.sortedRows.map((row, index) => (
                                <tr
                                  key={`${row.code || "row"}-${index}`}
                                  className="border-t border-slate-100"
                                >
                                  <td className="px-4 py-3 report-td-left text-slate-700">
                                    {row.issued_at_local || "--"}
                                  </td>
                                  <td className="px-4 py-3 report-td-left text-slate-700">
                                    {row.code || "--"}
                                  </td>
                                  <td className="px-4 py-3 report-td-left text-slate-700">
                                    {row.client_name || "Anonymous"}
                                  </td>
                                  <td className="px-4 py-3 report-td-center text-slate-700">
                                    {formatMoney(row.issued_amount)}
                                  </td>
                                  <td className="px-4 py-3 report-td-left text-slate-700">
                                    {row.sold_by_employee || "--"}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {activeTab === "balancesAsOf" && (
                <div className="space-y-6">
                <div className="flex flex-wrap items-center gap-2">
                    {renderInlineLabel("Group By", "group_by")}
                    <button
                      type="button"
                      className={togglePillClass(groupBy === "gift_card")}
                      onClick={() => setGroupBy("gift_card")}
                    >
                      Gift Card
                    </button>
                    <button
                      type="button"
                      className={togglePillClass(groupBy === "client")}
                      onClick={() => setGroupBy("client")}
                    >
                      Client
                    </button>
                  </div>

                  {!activeData ? (
                    emptyState
                  ) : (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                          <div className="text-xs uppercase text-slate-500">
                            Count
                          </div>
                          <div className="text-2xl font-semibold text-slate-900">
                            {formatNumber(activeData?.totals?.count, 0)}
                          </div>
                        </div>
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                          <div className="text-xs uppercase text-slate-500">
                            Total Balance
                          </div>
                          <div className="text-2xl font-semibold text-slate-900">
                            {formatMoney(activeData?.totals?.total_balance)}
                          </div>
                        </div>
                      </div>

                      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                          <div className="text-sm font-semibold text-slate-700">
                            Balances As Of
                          </div>
                          <button
                            type="button"
                            className={BTN_EXPORT}
                          onClick={() => {
                            const table =
                              groupBy === "client"
                                  ? buildBalancesClientTable(balancesRows || [])
                                  : buildBalancesGiftCardTable(balancesRows || []);
                            exportCsv(
                              `gift_card_balances_as_of_${asOfDate}.csv`,
                              table.columns,
                                table.rows
                              );
                            }}
                          >
                            Export CSV
                          </button>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="min-w-full text-sm report-table">
                            <thead className="bg-slate-50 text-slate-600">
                              {groupBy === "client" ? (
                                <tr>
                                  <SortableTh
                                    label="Client"
                                    sortKey="client"
                                    sortState={balancesClientSort.sortState}
                                    onSort={balancesClientSort.handleSort}
                                    tooltip={GIFT_CARD_ANALYTICS_TOOLTIPS.column_client}
                                    className="px-4 py-3 report-th-left"
                                    align="left"
                                  />
                                  <SortableTh
                                    label="Gift Cards Count"
                                    sortKey="gift_cards_count"
                                    sortState={balancesClientSort.sortState}
                                    onSort={balancesClientSort.handleSort}
                                    tooltip={GIFT_CARD_ANALYTICS_TOOLTIPS.column_gift_cards_count}
                                    className="px-4 py-3 report-th-center"
                                    align="center"
                                  />
                                  <SortableTh
                                    label="Total Balance As Of"
                                    sortKey="balance"
                                    sortState={balancesClientSort.sortState}
                                    onSort={balancesClientSort.handleSort}
                                    tooltip={GIFT_CARD_ANALYTICS_TOOLTIPS.column_total_balance}
                                    className="px-4 py-3 report-th-center"
                                    align="center"
                                  />
                                </tr>
                              ) : (
                                <tr>
                                  <SortableTh
                                    label="Gift Card Code/ID"
                                    sortKey="code"
                                    sortState={balancesGiftCardSort.sortState}
                                    onSort={balancesGiftCardSort.handleSort}
                                    tooltip={GIFT_CARD_ANALYTICS_TOOLTIPS.column_code}
                                    className="px-4 py-3 report-th-left"
                                    align="left"
                                  />
                                  <SortableTh
                                    label="Client"
                                    sortKey="client"
                                    sortState={balancesGiftCardSort.sortState}
                                    onSort={balancesGiftCardSort.handleSort}
                                    tooltip={GIFT_CARD_ANALYTICS_TOOLTIPS.column_client}
                                    className="px-4 py-3 report-th-left"
                                    align="left"
                                  />
                                  <SortableTh
                                    label="Balance As Of"
                                    sortKey="balance"
                                    sortState={balancesGiftCardSort.sortState}
                                    onSort={balancesGiftCardSort.handleSort}
                                    tooltip={GIFT_CARD_ANALYTICS_TOOLTIPS.column_balance}
                                    className="px-4 py-3 report-th-center"
                                    align="center"
                                  />
                                  <SortableTh
                                    label="Last Activity"
                                    sortKey="last_activity"
                                    sortState={balancesGiftCardSort.sortState}
                                    onSort={balancesGiftCardSort.handleSort}
                                    tooltip={GIFT_CARD_ANALYTICS_TOOLTIPS.column_last_activity}
                                    className="px-4 py-3 report-th-left"
                                    align="left"
                                  />
                                </tr>
                              )}
                            </thead>
                            <tbody>
                              {balancesRows.map((row, index) =>
                                groupBy === "client" ? (
                                  <tr
                                    key={`${row.client_id || "row"}-${index}`}
                                    className="border-t border-slate-100"
                                  >
                                    <td className="px-4 py-3 report-td-left text-slate-700">
                                      {row.client_name || "Anonymous"}
                                    </td>
                                    <td className="px-4 py-3 report-td-center text-slate-700">
                                      {formatNumber(row.gift_cards_count, 0)}
                                    </td>
                                    <td className="px-4 py-3 report-td-center text-slate-700">
                                      {formatMoney(row.total_balance_as_of)}
                                    </td>
                                  </tr>
                                ) : (
                                  <tr
                                    key={`${row.code || "row"}-${index}`}
                                    className="border-t border-slate-100"
                                  >
                                    <td className="px-4 py-3 report-td-left text-slate-700">
                                      {row.code || "--"}
                                    </td>
                                    <td className="px-4 py-3 report-td-left text-slate-700">
                                      {row.client_name || "Anonymous"}
                                    </td>
                                    <td className="px-4 py-3 report-td-center text-slate-700">
                                      {formatMoney(row.balance_as_of)}
                                    </td>
                                    <td className="px-4 py-3 report-td-left text-slate-700">
                                      {row.last_activity_local || "--"}
                                    </td>
                                  </tr>
                                )
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {activeTab === "clientsWithBalance" && (
                <div className="space-y-6">
                  {!activeData ? (
                    emptyState
                  ) : (
                    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                        <div className="text-sm font-semibold text-slate-700">
                          Clients With Balance
                        </div>
                        <button
                          type="button"
                          className={BTN_EXPORT}
                          onClick={() => {
                            const table = buildClientsWithBalanceTable(
                              clientsWithBalanceSort.sortedRows || []
                            );
                            exportCsv(
                              `gift_card_clients_with_balance_${startDate}_to_${endDate}.csv`,
                              table.columns,
                              table.rows
                            );
                          }}
                        >
                          Export CSV
                        </button>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm report-table">
                          <thead className="bg-slate-50 text-slate-600">
                            <tr>
                              <SortableTh
                                label="Client"
                                sortKey="client"
                                sortState={clientsWithBalanceSort.sortState}
                                onSort={clientsWithBalanceSort.handleSort}
                                tooltip={GIFT_CARD_ANALYTICS_TOOLTIPS.column_client}
                                className="px-4 py-3 report-th-left"
                                align="left"
                              />
                              <SortableTh
                                label="Total Balance As Of End Date"
                                sortKey="balance"
                                sortState={clientsWithBalanceSort.sortState}
                                onSort={clientsWithBalanceSort.handleSort}
                                tooltip={GIFT_CARD_ANALYTICS_TOOLTIPS.column_total_balance}
                                className="px-4 py-3 report-th-center"
                                align="center"
                              />
                              <SortableTh
                                label="Gift Cards Count"
                                sortKey="gift_cards_count"
                                sortState={clientsWithBalanceSort.sortState}
                                onSort={clientsWithBalanceSort.handleSort}
                                tooltip={GIFT_CARD_ANALYTICS_TOOLTIPS.column_gift_cards_count}
                                className="px-4 py-3 report-th-center"
                                align="center"
                              />
                              <SortableTh
                                label="Last Activity"
                                sortKey="last_activity"
                                sortState={clientsWithBalanceSort.sortState}
                                onSort={clientsWithBalanceSort.handleSort}
                                tooltip={GIFT_CARD_ANALYTICS_TOOLTIPS.column_last_activity}
                                className="px-4 py-3 report-th-left"
                                align="left"
                              />
                            </tr>
                          </thead>
                          <tbody>
                            {clientsWithBalanceSort.sortedRows.map((row, index) => (
                              <tr
                                key={`${row.client_id || "row"}-${index}`}
                                className="border-t border-slate-100"
                              >
                                <td className="px-4 py-3 report-td-left text-slate-700">
                                  {row.client_name || "Anonymous"}
                                </td>
                                <td className="px-4 py-3 report-td-center text-slate-700">
                                  {formatMoney(row.total_balance_as_of_end_date)}
                                </td>
                                <td className="px-4 py-3 report-td-center text-slate-700">
                                  {formatNumber(row.gift_cards_count, 0)}
                                </td>
                                <td className="px-4 py-3 report-td-left text-slate-700">
                                  {row.last_activity_local || "--"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === "salesRedemptions" && (
                <div className="space-y-6">
                  {!activeData ? (
                    emptyState
                  ) : (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                          <div className="text-xs uppercase text-slate-500">
                            Issued Total
                          </div>
                          <div className="text-2xl font-semibold text-slate-900">
                            {formatMoney(activeData?.issued_total)}
                          </div>
                        </div>
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                          <div className="text-xs uppercase text-slate-500">
                            Redeemed Total
                          </div>
                          <div className="text-2xl font-semibold text-slate-900">
                            {formatMoney(activeData?.redeemed_total)}
                          </div>
                        </div>
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                          <div className="text-xs uppercase text-slate-500">
                            Adjustments Total
                          </div>
                          <div className="text-2xl font-semibold text-slate-900">
                            {formatMoney(activeData?.adjustments_total)}
                          </div>
                        </div>
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                          <div className="text-xs uppercase text-slate-500">
                            Net Change
                          </div>
                          <div className="text-2xl font-semibold text-slate-900">
                            {formatMoney(activeData?.net_change)}
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {activeTab === "liabilitySummary" && (
                <div className="space-y-6">
                  {!activeData ? (
                    emptyState
                  ) : (
                    <>
                      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                        <div className="text-xs uppercase text-slate-500">
                          Liability As Of Date
                        </div>
                        <div className="text-3xl font-semibold text-slate-900">
                          {formatMoney(activeData?.liability_as_of)}
                        </div>
                      </div>

                      {Array.isArray(activeData?.top_clients_by_balance) &&
                        activeData.top_clients_by_balance.length > 0 && (
                          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                              <div className="text-sm font-semibold text-slate-700">
                                Top Clients by Balance
                              </div>
                              <button
                                type="button"
                                className={BTN_EXPORT}
                                onClick={() => {
                                  const table = buildTopClientsTable(
                                    topClientsSort.sortedRows || []
                                  );
                                  exportCsv(
                                    `gift_card_top_clients_${asOfDate}.csv`,
                                    table.columns,
                                    table.rows
                                  );
                                }}
                              >
                                Export CSV
                              </button>
                            </div>
                            <div className="overflow-x-auto">
                              <table className="min-w-full text-sm report-table">
                                <thead className="bg-slate-50 text-slate-600">
                                  <tr>
                                    <SortableTh
                                      label="Client"
                                      sortKey="client"
                                      sortState={topClientsSort.sortState}
                                      onSort={topClientsSort.handleSort}
                                      tooltip={GIFT_CARD_ANALYTICS_TOOLTIPS.column_client}
                                      className="px-4 py-3 report-th-left"
                                      align="left"
                                    />
                                    <SortableTh
                                      label="Balance"
                                      sortKey="balance"
                                      sortState={topClientsSort.sortState}
                                      onSort={topClientsSort.handleSort}
                                      tooltip={GIFT_CARD_ANALYTICS_TOOLTIPS.column_balance}
                                      className="px-4 py-3 report-th-center"
                                      align="center"
                                    />
                                  </tr>
                                </thead>
                                <tbody>
                                  {topClientsSort.sortedRows.map(
                                    (row, index) => (
                                      <tr
                                        key={`${row.client_id || "row"}-${index}`}
                                        className="border-t border-slate-100"
                                      >
                                        <td className="px-4 py-3 report-td-left text-slate-700">
                                          {row.client_name || "Client"}
                                        </td>
                                        <td className="px-4 py-3 report-td-center text-slate-700">
                                          {formatMoney(row.total_balance)}
                                        </td>
                                      </tr>
                                    )
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
